// 05-11 (auditoría, QA M6) — abrir la oficina cuando el panel no contesta. Si la primera carga (GET /api/oficina/estado)
// fallaba (500/502: pasa en cada subida del panel), la página se quedaba para siempre en «Abriendo la oficina…»: decía
// «se reintenta al reconectar», pero el socket estaba vivo y no reconectaba nunca. Ahora lo dice en cristiano, lo vuelve
// a intentar solo cada vez más espaciado (2 · 4 · 8 · 30 s, y luego cada 30 s) y deja «Reintentar ahora».
import { useEffect, useState } from 'react';
import { t } from '../../../i18n';
import { useOficinaStore } from '../store';
import { Boton } from '../ui';

/** Esperas entre intentos (s); el último se repite. */
export const ESPERAS_ARRANQUE_S = [2, 4, 8, 30] as const;

export function Arranque({ movil = false }: { movil?: boolean }) {
  const error = useOficinaStore((s) => s.error);
  const hidratar = useOficinaStore((s) => s.hidratar);
  const [intento, setIntento] = useState(0);
  const [quedan, setQuedan] = useState<number | null>(null);
  const [intentando, setIntentando] = useState(false);

  const reintentar = () => {
    setIntentando(true);
    setQuedan(null);
    void hidratar().finally(() => {
      setIntentando(false);
      setIntento((n) => n + 1);
    });
  };

  // Con el error a la vista, la cuenta atrás hasta el siguiente intento (un repintado por segundo).
  useEffect(() => {
    if (!error || intentando) return;
    const espera: number = ESPERAS_ARRANQUE_S[Math.min(intento, ESPERAS_ARRANQUE_S.length - 1)]!;
    setQuedan(espera);
    const inicio = Date.now();
    const reloj = setInterval(() => {
      const resto = espera - Math.floor((Date.now() - inicio) / 1000);
      if (resto > 0) setQuedan(resto);
      else {
        clearInterval(reloj);
        reintentar();
      }
    }, 1000);
    return () => clearInterval(reloj);
    // `reintentar` es nuevo en cada render; lo que manda es el error, el intento y si hay uno en marcha.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error, intento, intentando]);

  const fondo = movil ? 'flex h-full flex-col items-center justify-center gap-3 px-6 text-center' : 'flex h-dvh flex-col items-center justify-center gap-3 bg-oficina-fondo px-6 text-center';
  if (!error) return <p className={`${fondo} text-o-texto text-oficina-texto3`}>{t('oficinaV2.abriendo')}</p>;
  return (
    <div role="alert" className={fondo} data-testid="arranque-error">
      <p className="text-o-titulo3 font-semibold text-oficina-texto">{t('oficinaV2.arranque.error')}</p>
      <p className="text-o-texto text-oficina-texto2" aria-live="polite">
        {intentando || quedan === null ? t('oficinaV2.arranque.intentando') : t('oficinaV2.arranque.reintento', { n: quedan })}
      </p>
      <Boton onClick={reintentar} cargando={intentando} data-testid="arranque-reintentar">
        {t('oficinaV2.arranque.reintentar')}
      </Boton>
    </div>
  );
}
